import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiDelete, apiFetch, apiPost, apiPut } from "@/lib/api";
import { contactKeys } from "@/lib/query";

interface Contact {
  id: string;
  firstName: string | null;
  lastName: string | null;
  title: string | null;
  email: string | null;
  phone: string | null;
  type: string | null;
  companyId: string | null;
  companyName: string | null;
  companyType?: string | null;
  userId: string | null;
  isActive: boolean;
}

export type { Contact };

export interface AuthUserSummary {
  id: string;
  name: string;
  email: string;
  role: string;
  status: "active" | "banned";
}

export function useAuthUser(id: string | null | undefined) {
  return useQuery({
    queryKey: ["auth-user", id],
    queryFn: () => apiFetch<AuthUserSummary>(`/auth-users/${id}`),
    enabled: !!id,
  });
}

export function useSearchAuthUsers(query: string) {
  return useQuery({
    queryKey: ["auth-users", "search", query],
    queryFn: () => apiFetch<AuthUserSummary[]>(`/auth-users/search?q=${encodeURIComponent(query)}`),
  });
}

export function useContacts() {
  return useQuery({
    queryKey: contactKeys.all,
    queryFn: () => apiFetch<Contact[]>("/contacts"),
  });
}

export function useContact(id: string) {
  return useQuery({
    queryKey: contactKeys.detail(id),
    queryFn: () => apiFetch<Contact>(`/contacts/${id}`),
    enabled: !!id,
  });
}

export interface CreateContactResponse {
  data: Contact;
  duplicate?: boolean;
}

export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Contact>) => apiPost<CreateContactResponse>("/contacts", data),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: contactKeys.all });
      if (res.duplicate) {
        const name =
          [res.data.firstName, res.data.lastName].filter(Boolean).join(" ") || res.data.email;
        toast.message(`Existing contact found: ${name}`);
      } else {
        toast.success("Contact created");
      }
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<Contact>) =>
      apiPut<{ data: Contact }>(`/contacts/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contactKeys.all });
      toast.success("Contact updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useDeleteContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/contacts/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contactKeys.all });
      toast.success("Contact deleted");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useContactsByCompany(companyId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: contactKeys.byCompany(companyId ?? ""),
    queryFn: () => apiFetch<Contact[]>(`/contacts?companyId=${companyId}`),
    enabled: enabled && !!companyId,
  });
}

export function useProjectContacts(projectId: string) {
  return useQuery({
    queryKey: contactKeys.projectContacts(projectId),
    queryFn: () => apiFetch<Contact[]>(`/projects/${projectId}/contacts`),
    enabled: !!projectId,
  });
}

export interface AddProjectContactResponse {
  data: unknown;
  alreadyAssigned?: boolean;
}

export function useAddProjectContact(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contactId: string) =>
      apiPost<AddProjectContactResponse>(`/projects/${projectId}/contacts`, { contactId }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: contactKeys.projectContacts(projectId) });
      if (res.alreadyAssigned) {
        toast.message("Already on this project");
      } else {
        toast.success("Contact added to project");
      }
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

/**
 * Composite: create-or-find a directory contact by email, then assign to the project.
 * If the email already exists in the directory, the existing contact is linked instead
 * (the user's chosen "auto-link existing" UX).
 */
export function useAddNewContactToProject(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<Contact>) => {
      const created = await apiPost<CreateContactResponse>("/contacts", data);
      const assigned = await apiPost<AddProjectContactResponse>(`/projects/${projectId}/contacts`, {
        contactId: created.data.id,
      });
      return { ...created, assigned };
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: contactKeys.all });
      qc.invalidateQueries({ queryKey: contactKeys.projectContacts(projectId) });
      const name = [res.data.firstName, res.data.lastName].filter(Boolean).join(" ") || "Contact";
      if (res.duplicate) {
        toast.success(`Existing contact linked: ${name}`);
      } else {
        toast.success(`${name} added to project`);
      }
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useRemoveProjectContact(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (contactId: string) => apiDelete(`/projects/${projectId}/contacts/${contactId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contactKeys.projectContacts(projectId) });
      toast.success("Contact removed from project");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
